import React from "react"
import { withPrefix } from "gatsby"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import Slider from "../../components/slider"
import MemberVoices from "../../components/member-voices"
import ReadNext from "../../components/read-next"
import Stat from "../../components/stat"
import CovidHero from "../../components/covid-hero"
import Image from "../../components/image"
import Video from "../../components/video"
import { AppendixWaypoint, Appendix } from "../../components/appendix"

const PAGESLUG = "covid-19"

export default function Covid19() {
  return (
    <Layout>
      <SubNavigation id="A Crisis Like No Other" current={PAGESLUG} />
      <CovidHero />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium">
            <h2 className="page__title--opener util__text-shadow-white">
              Swift action helped fight the pandemic and limit the damage to
              people’s lives and the global economy
            </h2>
            <h3 className="page__title util__text-shadow-white">Covid-19</h3>
            <p className="util__text-shadow-white">
              It has been a crisis like no other. To tackle the health
              emergency, countries had to bring economic life to a standstill
              during the Great Lockdown. This created the worst recession since
              the Great Depression. The IMF acted swiftly to help people and
              countries, while enormous uncertainty clouded the prospects for
              the global economy and the world continued to wrestle with the
              pandemic’s unknowns.
            </p>
            <ul className="page__columns page__columns--3">
              <li>
                <Stat
                  prefix="$"
                  number={1}
                  unit="trillion"
                  note="in lending capacity, available to IMF’s membership"
                />
              </li>
              <li>
                <Stat
                  prefix="$"
                  number={165}
                  unit="billion"
                  note="in financial assistance to..."
                />
              </li>
              <li>
                <Stat number={83} unit="countries" />
              </li>
            </ul>
            <p>
              The crisis has upended people’s lives in countless ways. In
              addition to the tragic loss of life, millions of people have lost
              jobs, income, and savings, and many worry about how to pay their
              rent and bills.
            </p>
            <p>
              The magnitude and speed of the economic collapse were
              unprecedented. The crisis undermined global financial stability,
              and large segments of the global economy ground to a standstill,
              including the informal economy, which remains large in Latin
              America and sub-Saharan Africa. To save lives, governments
              financed additional health and emergency services. Where
              conditions and room in the budget allowed, governments also
              stopped the freefall of global growth with extraordinary monetary
              and fiscal support—the latter to the tune of $11.5 trillion
              globally as of September 2020—to extend lifelines to businesses
              and people.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={3} />
            <p>
              These exceptional times required equally exceptional, quick
              action. The IMF has worked to help protect people, help protect
              the economy, and help countries prepare for the recovery.
            </p>
          </div>
        </div>
      </section>
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium">
            <h2 className="page__title">Lending a hand</h2>
            <p>
              Countries all over the world faced urgent and unprecedented
              balance of payments and financing needs, which created an
              immediate and record-breaking demand for IMF resources.
            </p>
            <p>
              In response to the crisis, the IMF shifted work priorities to
              focus on the most critical aspects, streamlined procedures to
              speed up decision making, and deployed staff to new assignments
              where they were most needed.
            </p>
          </div>
          <div className="container container--full-width container--padding-none">
            <Slider id={PAGESLUG} autoplay={true} />
          </div>
          <div className="container container--medium">
            <h3 className="page__quote">
              The IMF has worked with other partners{" "}
              <em className="theme-color--light-blue">
                to preserve lives and livelihoods,
              </em>{" "}
              including by supporting the poorest and most vulnerable.
            </h3>
            <p>
              Critical governance and financing reforms approved by the
              Executive Board demonstrated the IMF’s readiness to support
              countries. These timely actions helped maintain the IMF’s $1
              trillion lending capacity so it can provide strong support as
              countries face unprecedented financing needs resulting from the
              pandemic.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={6} />
            <p>
              Overall, since May 1, 2019, the IMF has approved about $165
              billion in loans, including those predating the pandemic.
            </p>
            <ul className="page__columns page__columns--1">
              <li>
                <Stat number={196} unit="Economies" />
              </li>
            </ul>
            <a
              href="https://www.imf.org/en/Topics/imf-and-covid19/Policy-Responses-to-COVID-19"
              className="cta--primary"
            >
              <svg
                fill="none"
                height="35"
                viewBox="0 0 36 35"
                width="36"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                >
                  <path d="m17.6699 34c9.1127 0 16.5-7.3873 16.5-16.5s-7.3873-16.5-16.5-16.5c-9.11268 0-16.49998 7.3873-16.49998 16.5s7.3873 16.5 16.49998 16.5z" />
                  <path d="m1.16992 17.5h32.99998" />
                  <path d="m17.6713 1c4.1271 4.51828 6.4725 10.3819 6.6 16.5-.1275 6.1181-2.4729 11.9817-6.6 16.5-4.1271-4.5183-6.4726-10.3819-6.6-16.5.1274-6.1181 2.4729-11.98172 6.6-16.5z" />
                </g>
              </svg>
              View the Policy Tracker
            </a>
          </div>
        </div>
      </section>
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium">
            <h2 className="page__title">Transparency and trust</h2>
            <p>
              Governance and accountability could not take a back seat during
              the crisis and have become more important than ever.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={7} />
            <p>
              Governments around the world have undertaken major fiscal and
              financial measures to provide lifelines to people and firms. Such
              rapid expansions in the role of the government, however, creates
              opportunities for corruption, as past crises have shown. This
              means governments need to control and oversee emergency fiscal and
              financial measures. The IMF’s advice has been to spend whatever it
              takes but keep the receipts.
            </p>
            <p>
              For the IMF’s emergency financing to help countries address
              COVID-19, the right safeguards help balance the need to ensure
              that the funds reach those who need them most with the need to
              disburse them quickly. Borrowing countries are committed to
            </p>
            <ol className="page__list page__list--ordered">
              <li className="title--quaternary title--bold">
                undertaking and publishing independent ex post audits of
                crisis-related spending and
              </li>
              <li className="title--quaternary title--bold">
                publishing crisis-related procurement contracts on the
                government’s website, including identifying the companies
                awarded the contracts and their beneficial owners.
              </li>
            </ol>
            <p>
              The IMF also ensured that emergency resources are subject to the
              Safeguards Assessment policy.
            </p>
            <p>
              The IMF’s 2018 Framework for Enhanced Fund Engagement on
              Governance is part of the ongoing comprehensive effort to improve
              member countries’ good governance and efforts to tackle
              corruption. The pandemic has heightened the importance of{" "}
              <a href="https://www.transparency.org/en/blog/the-imf-on-corruption-and-covid-19">
                stronger governance
              </a>
              , and the Framework underwent interim reviews in mid-2020.
            </p>
          </div>
          <div className="container container--medium container--padding-v-none">
            <MemberVoices id={PAGESLUG} />
          </div>
        </div>
      </section>
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium container--padding-bottom-none">
            <h2 className="page__title">Global cooperation</h2>
            <p>
              The heart of the IMF’s mission is to promote cooperation between
              countries. Sharing information, data, research, and analysis of
              member countries’ policies is a major feature of the engine room
              that powers the institution’s work.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={8} />
            <p>
              For example, to provide up-to-date, ongoing information on the
              policies implemented by countries to contain the pandemic and its
              damage to economies, the IMF created a{" "}
              <a href="https://www.imf.org/en/Topics/imf-and-covid19/Policy-Responses-to-COVID-19">
                Policy Tracker
              </a>{" "}
              that summarizes the key economic responses governments have taken
              to limit the human and economic impact of COVID-19. The tracker
              includes 196 economies and is updated regularly. In addition, the
              IMF has published a{" "}
              <a href="https://www.imf.org/en/Publications/SPROLLs/covid19-special-notes">
                Special Series of notes on COVID-19
              </a>{" "}
              to help policymakers address the economic effects of the pandemic.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={14} />
            <p>
              In addition, as a global leader among peers in the multilateral
              system, the IMF’s partnerships with other international
              organizations have formed an important part of the response to
              support all countries in this crisis.
            </p>
          </div>
          <div className="container container--large container--padding-v-none">
            <Video
              videoNative={
                withPrefix("/") + "video/covid-19/combating-the-crisis.mp4"
              }
              posterImage={
                withPrefix("/") +
                "video/covid-19/combating-the-crisis-poster.jpg"
              }
              caption="Learn how we are working together to fight the COVID-19 crisis."
            />
          </div>
          <div className="container container--medium container--padding-top-none">
            <p>
              The IMF, World Bank, and other partners, including the Group of
              20, called for creditors to suspend debt repayments to provide
              much-needed support to the poorest countries. This official
              bilateral debt moratorium, the Debt Service Suspension Initiative,
              relieved the poorest countries of billions of dollars in debt
              payments, which they could use for their health systems and to
              protect their citizens. In addition, the IMF is providing debt
              relief through the Catastrophe Containment and Relief Trust
              (CCRT). The IMF Executive Board has approved debt relief through
              the CCRT for 29 of its poorest and most vulnerable member
              countries on their IMF obligations.
            </p>
            <p>
              The IMF and the World Bank brought together African leaders,
              bilateral partners, and multilateral institutions during the April
              2020 Spring Meetings, and again in October 2020, to spur faster
              action on COVID-19 response in African countries. Multilateral
              organizations, including the United Nations, pledged their
              continued support, and bilateral partners reemphasized their
              commitment to a debt standstill beginning May 1, 2020. That
              standstill was extended in October 2020.
            </p>
            <p>
              The IMF and the World Health Organization stood together to
              highlight how the two organizations can work in tandem to preserve
              lives and the global economy. For the first time in the history of
              the IMF, epidemiologists provided input for economic projections.
            </p>
            <p>
              The IMF and the World Trade Organization called for more attention
              to the role of open trade policies—especially for food and medical
              supplies—in defeating the virus, restoring jobs, and
              reinvigorating economic growth.
            </p>
            <h3 className="page__quote">
              The IMF has{" "}
              <em className="theme-color--light-blue">
                partnered with other international organizations to
              </em>{" "}
              boost the response to COVID-19 around the globe.
            </h3>
            <AppendixWaypoint page={PAGESLUG} id={15} />
            <p>
              The IMF also coordinated with a number of Regional Financing
              Arrangements, including the European Stability Mechanism and the
              Arab Monetary Fund, among others. They are supporting their
              members through lending, adjusting policies and toolkits to make
              them compatible with the emergency nature of the COVID-19 crisis,
              and providing policy and technical advice to help their
              authorities through these challenging economic times. Regional
              rescue funds are closely coordinating with IMF country teams to
              exchange information and expertise necessary to expeditiously help
              countries facing the most pressing financing needs.
            </p>
          </div>
        </div>
      </section>
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium">
            <h2 className="page__title">Home-based operations</h2>
            <p>
              Through all this, the IMF, just like any workplace, had to adjust
              how it works, matching its own changes to the speed of the crisis.
            </p>
            <h3 className="page__quote">
              <em className="theme-color--light-blue">
                All our operations quickly became virtual,
              </em>{" "}
              from surveillance missions, to lending negotiations, to technical
              assistance and training.
            </h3>
            <p>
              IMF staff adjusted, including those based in Washington, DC, and
              in countries around the world. The IMF Board, management, and
              staff moved their operations from the boardroom and their offices
              into living rooms, kitchens, spare rooms, and basements. All work
              quickly became virtual, from surveillance missions, to lending
              negotiations, to technical assistance and training.
            </p>
            <p>
              Immediate and real-time policy advice and capacity development
              were provided virtually to over 160 countries on topics ranging
              from cash management and data to economic governance. More than 90
              percent of countries that requested pandemic-related emergency
              financing have also received capacity development support in the
              form of hands-on technical advice, practical tools, and
              policy-oriented training.
            </p>
            <p>
              After its first-ever virtual Spring Meetings in April 2020, the
              IMF continued its work virtually, meeting online with country
              officials to discuss programs, provide capacity development, and
              conduct Board meetings.
            </p>
            <Image
              path="spotlights/covid-19/home-operations.jpg"
              alt="A group of IMF staff working remotely with remote video software"
              caption="Remote meetings have become common practice at the IMF."
            />
            <hr />
            <p className="page__meta">
              INFORMATION IN THIS ARTICLE IS AS OF SEPTEMBER 15, 2020
            </p>
          </div>
        </div>
      </section>
      <Appendix page={PAGESLUG} />
      <ReadNext sectionID="spotlights" pageID="economics" />
    </Layout>
  )
}
